(() => {
  class DimensionRangeFilter extends HTMLElement {
    connectedCallback() {
      this.checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"]'));
      if (this.checkboxes.length < 2) return;

      this.unit = this.getAttribute('data-unit') || '';
      this.filterLabel = this.getAttribute('data-label') || '';

      this.values = this.checkboxes
        .map((input) => ({
          input,
          value: Number(input.value),
          count: Number(input.dataset.count || 0),
          disabled: input.disabled,
        }))
        .sort((a, b) => a.value - b.value);

      this.lastIndex = this.values.length - 1;

      this.buildUI();
      this.syncFromCheckboxes();
      this.applyIndices();
    }

    buildUI() {
      const min = this.values[0].value;
      const max = this.values[this.lastIndex].value;

      const wrap = document.createElement('div');
      wrap.className = 'dimension-slider';
      wrap.innerHTML = `
        <div class="dimension-slider__inputs">
          <label class="dimension-slider__field">
            <span class="dimension-slider__field-label">Min</span>
            <span class="dimension-slider__field-control">
              <input type="number" class="dimension-slider__number dimension-slider__number--min" min="${min}" max="${max}" step="1" inputmode="numeric">
              <span class="dimension-slider__unit">${this.unit}</span>
            </span>
          </label>
          <span class="dimension-slider__field-sep" aria-hidden="true">&ndash;</span>
          <label class="dimension-slider__field">
            <span class="dimension-slider__field-label">Max</span>
            <span class="dimension-slider__field-control">
              <input type="number" class="dimension-slider__number dimension-slider__number--max" min="${min}" max="${max}" step="1" inputmode="numeric">
              <span class="dimension-slider__unit">${this.unit}</span>
            </span>
          </label>
        </div>
        <div class="dimension-slider__track-wrap">
          <div class="dimension-slider__scale">
            <div class="dimension-slider__track"></div>
            <div class="dimension-slider__range"></div>
          </div>
          <input type="range" class="dimension-slider__range-input dimension-slider__range-input--min" min="0" max="${this.lastIndex}" step="1">
          <input type="range" class="dimension-slider__range-input dimension-slider__range-input--max" min="0" max="${this.lastIndex}" step="1">
        </div>
      `;
      this.prepend(wrap);

      this.rangeEl = wrap.querySelector('.dimension-slider__range');
      this.trackWrap = wrap.querySelector('.dimension-slider__track-wrap');
      this.minRange = wrap.querySelector('.dimension-slider__range-input--min');
      this.maxRange = wrap.querySelector('.dimension-slider__range-input--max');
      this.minNumber = wrap.querySelector('.dimension-slider__number--min');
      this.maxNumber = wrap.querySelector('.dimension-slider__number--max');

      this.minRange.setAttribute('aria-label', `Minimum ${this.filterLabel}`);
      this.maxRange.setAttribute('aria-label', `Maximum ${this.filterLabel}`);

      this.minRange.addEventListener('input', (e) => { e.stopPropagation(); this.onRangeInput('min'); });
      this.maxRange.addEventListener('input', (e) => { e.stopPropagation(); this.onRangeInput('max'); });
      this.minRange.addEventListener('change', (e) => { e.stopPropagation(); this.commit(); });
      this.maxRange.addEventListener('change', (e) => { e.stopPropagation(); this.commit(); });
      this.minRange.addEventListener('pointerdown', () => this.bringToFront('min'));
      this.maxRange.addEventListener('pointerdown', () => this.bringToFront('max'));

      this.minNumber.addEventListener('input', (e) => e.stopPropagation());
      this.maxNumber.addEventListener('input', (e) => e.stopPropagation());
      this.minNumber.addEventListener('change', (e) => { e.stopPropagation(); this.onNumberChange('min'); });
      this.maxNumber.addEventListener('change', (e) => { e.stopPropagation(); this.onNumberChange('max'); });
      this.minNumber.addEventListener('keydown', (e) => this.onNumberKeyDown(e));
      this.maxNumber.addEventListener('keydown', (e) => this.onNumberKeyDown(e));

      this.trackWrap.addEventListener('pointerdown', (e) => this.onTrackPointerDown(e));
    }

    indexToPercent(index) {
      if (this.lastIndex === 0) return 0;
      return (index / this.lastIndex) * 100;
    }

    percentToIndex(percent) {
      const raw = Math.round((percent / 100) * this.lastIndex);
      return Math.max(0, Math.min(this.lastIndex, raw));
    }

    bringToFront(which) {
      this.minRange.classList.toggle('is-front', which === 'min');
      this.maxRange.classList.toggle('is-front', which === 'max');
    }

    syncFromCheckboxes() {
      const checkedIndexes = this.values
        .map((v, i) => (v.input.checked ? i : -1))
        .filter((i) => i !== -1);

      if (checkedIndexes.length) {
        this.minIndex = Math.min(...checkedIndexes);
        this.maxIndex = Math.max(...checkedIndexes);
      } else {
        this.minIndex = 0;
        this.maxIndex = this.lastIndex;
      }
    }

    nearestEnabledIndex(index, direction) {
      index = Math.max(0, Math.min(this.lastIndex, index));
      if (!this.values[index].disabled) return index;

      for (let step = 1; step <= this.lastIndex; step++) {
        const ahead = index + step * (direction < 0 ? -1 : 1);
        const behind = index - step * (direction < 0 ? -1 : 1);
        if (ahead >= 0 && ahead <= this.lastIndex && !this.values[ahead].disabled) return ahead;
        if (behind >= 0 && behind <= this.lastIndex && !this.values[behind].disabled) return behind;
      }
      return index;
    }

    valueToNearestIndex(rawValue) {
      let nearest = 0;
      let smallestDiff = Infinity;
      this.values.forEach((v, i) => {
        const diff = Math.abs(v.value - rawValue);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          nearest = i;
        }
      });
      return nearest;
    }

    applyIndices() {
      this.minRange.value = this.minIndex;
      this.maxRange.value = this.maxIndex;
      this.minNumber.value = this.values[this.minIndex].value;
      this.maxNumber.value = this.values[this.maxIndex].value;
      this.updateVisuals();
    }

    updateVisuals() {
      const minPercent = this.indexToPercent(this.minIndex);
      const maxPercent = this.indexToPercent(this.maxIndex);
      this.rangeEl.style.left = minPercent + '%';
      this.rangeEl.style.width = (maxPercent - minPercent) + '%';
    }

    onRangeInput(which) {
      let minIdx = parseInt(this.minRange.value, 10);
      let maxIdx = parseInt(this.maxRange.value, 10);

      if (which === 'min' && minIdx > maxIdx) {
        minIdx = maxIdx;
      } else if (which === 'max' && maxIdx < minIdx) {
        maxIdx = minIdx;
      }

      if (which === 'min') {
        minIdx = this.nearestEnabledIndex(minIdx, -1);
      } else {
        maxIdx = this.nearestEnabledIndex(maxIdx, 1);
      }

      this.minIndex = Math.min(minIdx, maxIdx);
      this.maxIndex = Math.max(minIdx, maxIdx);
      this.applyIndices();
    }

    onTrackPointerDown(event) {
      if (event.target !== this.trackWrap && event.target !== this.rangeEl) return;

      const rect = this.trackWrap.getBoundingClientRect();
      const percent = ((event.clientX - rect.left) / rect.width) * 100;
      const index = this.percentToIndex(percent);
      const which = Math.abs(index - this.minIndex) <= Math.abs(index - this.maxIndex) ? 'min' : 'max';

      this.bringToFront(which);
      if (which === 'min') {
        this.minIndex = Math.min(this.nearestEnabledIndex(index, -1), this.maxIndex);
      } else {
        this.maxIndex = Math.max(this.nearestEnabledIndex(index, 1), this.minIndex);
      }
      this.applyIndices();
      this.commit();
    }

    onNumberChange(which) {
      const input = which === 'min' ? this.minNumber : this.maxNumber;
      const raw = Number(input.value);

      if (Number.isNaN(raw) || input.value === '') {
        this.applyIndices();
        return;
      }

      let idx = this.valueToNearestIndex(raw);
      idx = this.nearestEnabledIndex(idx, which === 'min' ? -1 : 1);

      if (which === 'min') {
        this.minIndex = Math.min(idx, this.maxIndex);
      } else {
        this.maxIndex = Math.max(idx, this.minIndex);
      }

      this.applyIndices();
      this.commit();
    }

    onNumberKeyDown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.target.blur();
      }
    }

    commit() {
      const isFullRange = this.minIndex === 0 && this.maxIndex === this.lastIndex;
      const form = this.closest('form');
      let changed = false;

      this.values.forEach((v, i) => {
        const shouldBeChecked = !isFullRange && i >= this.minIndex && i <= this.maxIndex && !v.disabled;
        if (v.input.checked !== shouldBeChecked) {
          v.input.checked = shouldBeChecked;
          changed = true;
        }

        // The desktop and mobile filter panels each render their own copy of these
        // checkboxes under the same name/value. The form reads every checkbox with a
        // given name when it submits, so the sibling copy (e.g. mobile, when this drag
        // happened on desktop) must be kept in lockstep immediately, not just after the
        // next AJAX re-render - otherwise its stale checked state silently overrides
        // values this drag just unchecked. Mirrors the theme's own price-range fix.
        if (form) {
          form.querySelectorAll(`input[name="${CSS.escape(v.input.name)}"][value="${CSS.escape(v.input.value)}"]`).forEach((sibling) => {
            if (sibling !== v.input) sibling.checked = shouldBeChecked;
          });
        }
      });

      if (changed) {
        this.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  if (!customElements.get('dimension-range-filter')) {
    customElements.define('dimension-range-filter', DimensionRangeFilter);
  }
})();
